import { useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { startOfMonth, format } from 'date-fns';
import type { DateRange } from 'react-day-picker';
import { api } from '@/lib/api';
import { AppShell } from '@/components/app-shell';
import { useTenantMe } from '@/hooks/use-tenant-me';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Skeleton } from '@/components/ui/skeleton';

interface CategoryRow {
  category: string;
  total: number;
  count: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  alimentacion: 'Alimentación',
  transporte: 'Transporte',
  salud: 'Salud',
  educacion: 'Educación',
  vivienda: 'Gastos de Vivienda',
  servicios: 'Servicios',
  insumos: 'Insumos',
  otro: 'Otros',
};

const CATEGORY_COLORS: Record<string, string> = {
  alimentacion: 'bg-chart-1',
  transporte: 'bg-chart-2',
  salud: 'bg-chart-3',
  educacion: 'bg-chart-4',
  vivienda: 'bg-chart-5',
  servicios: 'bg-primary',
  insumos: 'bg-info',
  otro: 'bg-muted-foreground',
};

function formatGs(n: number): string {
  return `Gs ${Math.round(n).toLocaleString('es-PY')}`;
}

export function AppFinancesPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useTenantMe();
  const [range, setRange] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: new Date(),
  });
  const from = format(range?.from ?? startOfMonth(new Date()), 'yyyy-MM-dd');
  const to = format(range?.to ?? range?.from ?? new Date(), 'yyyy-MM-dd');

  const { data, isLoading } = useQuery({
    queryKey: ['app-finances', from, to],
    queryFn: () =>
      api<{ from: string; to: string; categories: CategoryRow[]; grandTotal: number }>(
        `/api/app/finances?from=${from}&to=${to}`
      ),
    enabled: !!me,
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/app/login');
    return null;
  }

  const total = data?.grandTotal ?? 0;
  const sorted = [...(data?.categories ?? [])].sort((a, b) => b.total - a.total);

  return (
    <AppShell>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Mis finanzas</h1>
          <p className="text-sm text-muted-foreground">
            Desglose de tus gastos por categoría según las facturas aprobadas.
          </p>
        </div>
        <DateRangePicker
          value={range}
          onChange={setRange}
          placeholder="Rango de fechas"
          align="end"
          disabled={{ after: new Date() }}
        />
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {data && sorted.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            Sin gastos categorizados en el período seleccionado.
          </CardContent>
        </Card>
      )}

      {data && sorted.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Desglose por categoría</CardTitle>
            <CardDescription>
              Total del período: <strong>{formatGs(total)}</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {sorted.map((c) => {
              const pct = total > 0 ? (c.total / total) * 100 : 0;
              const color = CATEGORY_COLORS[c.category] ?? 'bg-primary';
              return (
                <div key={c.category} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">
                      {CATEGORY_LABELS[c.category] ?? c.category}
                    </span>
                    <div className="text-right">
                      <span className="font-mono font-semibold">{formatGs(c.total)}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {pct.toFixed(1)}% · {c.count}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                    <div
                      className={'h-full ' + color}
                      style={{ width: `${Math.max(2, pct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </AppShell>
  );
}
